import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalBody, useToast } from '../sectepe-ui/index';
import companyService, { Company, Suppression } from '../services/companyService';
import { PageHeader, EmptyState, LoadingState, StatusBadge, useConfirm, StatusTone } from '../components/ui';
import { Icon } from '../components/icons';
import { SalesProcessBar } from '../vertrieb/SalesProcessBar';
import { useUrlParams } from '../vertrieb/useUrlParams';
import { datum, fehlerText } from '../vertrieb/format';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { RechteHinweis } from '../vertrieb/RechteHinweis';

/**
 * Firmenliste. Die Pflege selbst passiert in der Akte (/companies/:id) --
 * hier wird gesucht, angelegt und nachgetragen. Suche und Status stehen in
 * der URL, damit andere Seiten (Antworten, Pipeline) hierher verlinken
 * koennen: /companies?suche=Acme.
 *
 * Rechte (Paket 2): Anlegen braucht vertrieb.bearbeiten (Buchhaltung liest
 * die Liste nur); ohne Recht fehlt der Knopf "Neue Firma".
 */

export const FIRMEN_STATUS: { value: string; label: string; tone: StatusTone }[] = [
  { value: 'new', label: 'Neu', tone: 'info' },
  { value: 'reviewed', label: 'Gesichtet', tone: 'neutral' },
  { value: 'qualified', label: 'Qualifiziert', tone: 'success' },
  { value: 'ignored', label: 'Verworfen', tone: 'neutral' },
];

export const firmenStatus = (wert: string) =>
  FIRMEN_STATUS.find((s) => s.value === wert) ?? { value: wert, label: wert, tone: 'neutral' as StatusTone };

const CompaniesPage: React.FC = () => {
  const navigate = useNavigate();
  const { push } = useToast();
  const { confirm } = useConfirm();
  const { get, set, setMany } = useUrlParams();
  const { darf } = useBerechtigung();
  const darfAnlegen = darf('vertrieb.bearbeiten');
  // "Leads zuordnen" ist POST /companies/backfill (vertrieb.loeschen, Welle 1).
  const darfNachtragen = darf('vertrieb.loeschen');
  // Sperrliste: lesen mit vertrieb.lesen, Eintragen und Aufheben gehoeren zum
  // Versand (versand.ausfuehren, Kapitel 4.4) -- Mitarbeit und Marketing nicht.
  const darfSperren = darf('versand.ausfuehren');

  const suche = get('suche');
  const status = get('status');
  const [sucheEingabe, setSucheEingabe] = useState(suche);

  const [firmen, setFirmen] = useState<Company[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const [zeigeNeu, setZeigeNeu] = useState(false);
  const [neuName, setNeuName] = useState('');
  const [neuWebsite, setNeuWebsite] = useState('');
  const [legtAn, setLegtAn] = useState(false);

  const [zeigeSperrliste, setZeigeSperrliste] = useState(false);
  const [sperren, setSperren] = useState<Suppression[]>([]);
  const [neueSperre, setNeueSperre] = useState('');
  const [sperrGrund, setSperrGrund] = useState('');

  // Suche entprellt in die URL, sonst laedt jeder Tastendruck neu.
  useEffect(() => {
    const id = window.setTimeout(() => {
      if (sucheEingabe.trim() !== suche) set('suche', sucheEingabe.trim(), { replace: true });
    }, 300);
    return () => window.clearTimeout(id);
  }, [sucheEingabe, suche, set]);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      const liste = await companyService.list({
        suche: suche || undefined,
        status: status || undefined,
      });
      setFirmen(liste ?? []);
    } catch (e) {
      setFehler(fehlerText(e, 'Laden fehlgeschlagen'));
    } finally {
      setLaedt(false);
    }
  }, [suche, status]);

  useEffect(() => { laden(); }, [laden]);

  const anlegen = async () => {
    if (!neuName.trim()) return;
    setLegtAn(true);
    try {
      const firma = await companyService.create({
        name: neuName.trim(),
        website: neuWebsite.trim() || undefined,
      });
      push({ message: `„${firma.name}“ angelegt.`, severity: 'success' });
      // Direkt in die Akte: wer eine Firma anlegt, will sie als Naechstes fuellen.
      navigate(`/companies/${firma.id}`);
    } catch (e) {
      setFehler(fehlerText(e, 'Anlegen fehlgeschlagen'));
    } finally {
      setLegtAn(false);
    }
  };

  const nachtragen = async () => {
    setFehler(null);
    try {
      const b = await companyService.backfill();
      const teile = [
        `${b.geprueft} Leads geprüft`,
        `${b.firmenNeu} Firmen neu`,
        `${b.zugeordnet} zugeordnet`,
        `${b.ohneDomain} ohne verwertbare Domain`,
      ];
      if (b.aktivitaeten) teile.push(`${b.aktivitaeten.zugeordnet} Reaktionen in die Chronik eingehängt`);
      push({ message: teile.join(', '), severity: 'success', duration: 8000 });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Nachtrag fehlgeschlagen'));
    }
  };

  const sperrlisteLaden = async () => {
    try {
      setSperren((await companyService.getSuppressions()) ?? []);
      setZeigeSperrliste(true);
    } catch (e) {
      setFehler(fehlerText(e, 'Sperrliste konnte nicht geladen werden'));
    }
  };

  const sperreAnlegen = async () => {
    const wert = neueSperre.trim();
    if (!wert) return;
    try {
      await companyService.addSuppression(
        wert.includes('@') ? { email: wert, reason: sperrGrund } : { domain: wert, reason: sperrGrund },
      );
      setNeueSperre('');
      setSperrGrund('');
      setSperren((await companyService.getSuppressions()) ?? []);
      push({ message: `${wert} gesperrt.`, severity: 'success' });
    } catch (e) {
      setFehler(fehlerText(e, 'Sperre konnte nicht angelegt werden'));
    }
  };

  const sperreAufheben = async (s: Suppression) => {
    const ok = await confirm({
      title: 'Sperre aufheben?',
      message: `${s.email || s.domain} darf danach wieder angeschrieben werden.`,
      confirmLabel: 'Aufheben',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await companyService.removeSuppression(s.id);
      setSperren((await companyService.getSuppressions()) ?? []);
    } catch (e) {
      setFehler(fehlerText(e, 'Sperre konnte nicht aufgehoben werden'));
    }
  };

  const summen = useMemo(() => ({
    kontakte: firmen.reduce((s, f) => s + (f._count?.contacts ?? 0), 0),
    vorgaenge: firmen.reduce((s, f) => s + (f._count?.deals ?? 0), 0),
  }), [firmen]);

  const gefiltert = Boolean(suche || status);

  return (
    <div className="page-container-wide">
      <SalesProcessBar label="Firmen" />
      <PageHeader
        title="Firmen & Kontakte"
        subtitle="Mehrere Ansprechpartner je Haus, eine gemeinsame Akte. Firmen werden über die Domain geführt, nicht über den Namen."
        actions={(
          <>
            <button type="button" className="btn btn-secondary" onClick={sperrlisteLaden}>
              <Icon name="lock" size={14} /> Sperrliste
            </button>
            {darfNachtragen && (
              <button type="button" className="btn btn-secondary" onClick={nachtragen}>
                <Icon name="refresh" size={14} /> Leads zuordnen
              </button>
            )}
            {darfAnlegen && (
              <button type="button" className="btn btn-primary" onClick={() => setZeigeNeu((v) => !v)}>
                <Icon name="plus" size={14} /> Neue Firma
              </button>
            )}
          </>
        )}
      />

      <RechteHinweis was="Firmen anlegen und bearbeiten" />

      {fehler && <div className="alert alert-error">{fehler}</div>}

      {zeigeNeu && darfAnlegen && (
        <div className="quick-form" role="form" aria-label="Neue Firma">
          <label>
            Name
            <input
              autoFocus
              type="text"
              placeholder="z. B. Müller GmbH"
              value={neuName}
              onChange={(e) => setNeuName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') anlegen(); }}
            />
          </label>
          <label>
            Website
            <input
              type="text"
              placeholder="mueller.de"
              value={neuWebsite}
              onChange={(e) => setNeuWebsite(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') anlegen(); }}
            />
          </label>
          <div className="quick-form__actions">
            <button type="button" className="btn btn-primary" onClick={anlegen} disabled={!neuName.trim() || legtAn}>
              {legtAn ? 'Legt an…' : 'Anlegen & Akte öffnen'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setZeigeNeu(false)}>Abbrechen</button>
          </div>
        </div>
      )}

      <div className="toolbar" role="search">
        <div className="toolbar__group">
          <input
            type="search"
            placeholder="Name, Domain oder Branche"
            aria-label="Firmen durchsuchen"
            value={sucheEingabe}
            onChange={(e) => setSucheEingabe(e.target.value)}
          />
          <select value={status} onChange={(e) => set('status', e.target.value, { replace: true })} aria-label="Status filtern">
            <option value="">Alle Status</option>
            {FIRMEN_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {gefiltert && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSucheEingabe(''); setMany({ suche: null, status: null }, { replace: true }); }}>
              Filter zurücksetzen
            </button>
          )}
        </div>
        <div className="toolbar__group toolbar__group--right text-sm text-secondary">
          {!laedt && <span>{firmen.length} Firmen · {summen.kontakte} Kontakte · {summen.vorgaenge} Vorgänge</span>}
        </div>
      </div>

      {laedt ? (
        <LoadingState rows={5} label="Firmen werden geladen" />
      ) : firmen.length === 0 ? (
        <EmptyState
          icon="building"
          title={gefiltert ? 'Keine Firma passt zum Filter' : 'Noch keine Firmen'}
          description={gefiltert
            ? 'Andere Schreibweise probieren oder den Filter zurücksetzen.'
            : darfNachtragen
              ? '„Leads zuordnen“ legt Firmen aus den vorhandenen Kontakten und Reaktionen an. Oder du legst die erste von Hand an.'
              : 'Firmen entstehen aus den Kontakten und Reaktionen oder werden von Hand angelegt.'}
          action={gefiltert
            ? { label: 'Filter zurücksetzen', onClick: () => { setSucheEingabe(''); setMany({ suche: null, status: null }, { replace: true }); } }
            : darfNachtragen ? { label: 'Leads zuordnen', onClick: nachtragen } : undefined}
          secondaryAction={darfAnlegen ? { label: 'Neue Firma', onClick: () => setZeigeNeu(true) } : undefined}
        />
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Firma</th>
                <th>Domain</th>
                <th>Branche</th>
                <th className="r">Kontakte</th>
                <th className="r">Vorgänge</th>
                <th className="r">Reaktionen</th>
                <th>Status</th>
                <th>zuletzt</th>
              </tr>
            </thead>
            <tbody>
              {firmen.map((f) => {
                const st = firmenStatus(f.status);
                return (
                  <tr key={f.id} className="tabelle-zeile" onClick={() => navigate(`/companies/${f.id}`)}>
                    <td>
                      <Link to={`/companies/${f.id}`} className="akte-link" onClick={(e) => e.stopPropagation()}>{f.name}</Link>
                      {f.city && <div className="text-xs text-muted">{[f.postalCode, f.city].filter(Boolean).join(' ')}</div>}
                    </td>
                    <td>{f.domain || '—'}</td>
                    <td>{f.industry || '—'}</td>
                    <td className="r">{f._count?.contacts ?? 0}</td>
                    <td className="r">{f._count?.deals ?? 0}</td>
                    <td className="r">{f._count?.activities ?? 0}</td>
                    <td><StatusBadge tone={st.tone}>{st.label}</StatusBadge></td>
                    <td className="text-sm text-secondary">{datum(f.lastSeenAt, '')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={zeigeSperrliste} onOpenChange={(o) => { if (!o) setZeigeSperrliste(false); }}>
        <ModalContent className="detail-modal">
          <ModalHeader>
            <ModalTitle>Sperrliste</ModalTitle>
            <ModalDescription>
              Eine Sperre gilt vor jedem Versand und überlebt das Löschen und Neuanlegen eines Kontakts.
              Eine Domain sperrt das ganze Haus – für alle im Team.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {darfSperren && (
              <div className="quick-form">
                <label>
                  Adresse oder Domain
                  <input
                    type="text"
                    placeholder="adresse@firma.de oder firma.de"
                    value={neueSperre}
                    onChange={(e) => setNeueSperre(e.target.value)}
                  />
                </label>
                <label>
                  Grund
                  <input type="text" placeholder="z. B. Absage per Mail" value={sperrGrund} onChange={(e) => setSperrGrund(e.target.value)} />
                </label>
                <div className="quick-form__actions">
                  <button type="button" className="btn btn-primary" onClick={sperreAnlegen} disabled={!neueSperre.trim()}>Sperren</button>
                </div>
              </div>
            )}
            <RechteHinweis recht="versand.ausfuehren" was="Sperren anlegen und aufheben" />
            {sperren.length === 0 ? (
              <EmptyState compact icon="lock" title="Keine Sperren" description="Abmeldungen und Bounces aus den Kampagnen landen hier automatisch." />
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr><th>Adresse / Domain</th><th>Grund</th><th>seit</th>{darfSperren && <th />}</tr>
                  </thead>
                  <tbody>
                    {sperren.map((s) => (
                      <tr key={s.id}>
                        <td><strong>{s.email || s.domain}</strong></td>
                        <td>{s.reason || '—'}</td>
                        <td>{datum(s.createdAt)}</td>
                        {darfSperren && (
                          <td className="r">
                            <button type="button" className="btn btn-sm btn-secondary" onClick={() => sperreAufheben(s)}>aufheben</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default CompaniesPage;
